import SearchIconBtn from '../../assets/icons/SearchIconBtn'

import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import DownloadIcon from '../../assets/icons/DownloadIcon'
import { getColumns, getSearchFields } from '../../constants/tanColumn'
import { useSnackbar } from '../../contexts/SnackbarProvider'
import { ConfirmationDialog, Search } from '../common'
import CommonTable from '../common/CommonTable'
import { DEFAULT_PAGE, getValidCriteria } from '../../constants/index.js'
import { saveBlobAsFile } from "../../api/apiUtils.js"
import {
  deleteTanMaster,
  downloadTanMasterExcel,
  getEntityTypes,
  getTANList,
} from '../../api/adminTAN.js'

import { GLOBAL_CONSTANTS } from '../../constants/globalConstants.js'

const TAN = ({ roleConfig }) => {
  const navigate = useNavigate()
  const [tanData, setTanData] = useState([])
  const [entityTypes, setEntityTypes] = useState([])
  const columns = getColumns(entityTypes)
  const searchFields = getSearchFields(entityTypes)
  const [loading, setLoading] = useState(true)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [tanToDelete, setTanToDelete] = useState(null)
  const [startDownloading, setStartDownloading] = useState(false)
  // Search state
  const [searchOpen, setSearchOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const [searchCriteria, setSearchCriteria] = useState([
    {
      id: 1,
      logicalOperator: 'Where',
      field: searchFields[0]?.id || '',
      value: '',
    },
  ])

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(100)
  const [totalItems, setTotalItems] = useState(0)

  const { showSnackbar } = useSnackbar()

  const handleCloseSearch = () => {
    setSearchOpen(false)
    setAnchorEl(null)
    setSearchCriteria([
      {
        id: 1,
        logicalOperator: 'Where',
        field: searchFields[0]?.id || '',
        value: '',
      },
    ])
  }

  const fetchTanData = useCallback(
    async (page = 1, pageSize = itemsPerPage, searchList = []) => {
      setLoading(true)
      try {
        const payload = {
          pageFilter: {
            currentPage: page,
            includeCount: true,
            pageSize: pageSize,
            sortField: 'lastModifiedDate',
            sortOrder: 'DESC',
          },
          requestFilter: [],
          isReportDownload: false,
          searchCOList: searchList,
        }

        const response = await getTANList(payload)
        const newData = response?.content || []

        const mergedData = newData.map(item => ({
          ...item,
          ...(item.responsiblePersonDetailsResponseDTO || {}),
          mainObjID: item.uuid,
          responsiblePersonUuid: item.responsiblePersonDetailsResponseDTO?.uuid,
          id: item.uuid,
        }))

        setTanData(mergedData)
        setTotalItems(response.totalElements)
      } catch (error) {
        console.error('Error fetching TAN data:')
      } finally {
        setLoading(false)
      }
    },
    [itemsPerPage]
  )

  useEffect(() => {
    fetchTanData()
  }, [])

  useEffect(() => {
    const fetchEntityTypes = async () => {
      try {
        const response = await getEntityTypes()
        setEntityTypes(Array.isArray(response) ? response : [])
      } catch (error) {
        console.error('Error fetching entity types:')
      }
    }
    fetchEntityTypes()
  }, [])

  const handleAddTAN = () => {
    navigate('/admin/add-tan')
  }

  const handleEdit = row => {
    navigate(`/admin/edit-tan/${row.id}`)
  }

  const handleDelete = row => {
    setTanToDelete(row)
    setDeleteDialogOpen(true)
  }

  const confirmDelete = async () => {
    if (tanToDelete) {
      try {
        await deleteTanMaster(tanToDelete.mainObjID)
        setDeleteDialogOpen(false)
        setTanToDelete(null)
        fetchTanData(
          currentPage,
          itemsPerPage,
          getValidCriteria(searchCriteria)
        )
      } catch (error) {
        showSnackbar(error.message, 'error')
        console.error('Failed to delete TAN Master:')
      }
    }
  }

  const handleDownload = async () => {
    if (totalItems === 0) {
      showSnackbar('No Data Found', 'info')
      return
    }

    setStartDownloading(true)
    try {
      const payload = {
        pageFilter: {
          currentPage: currentPage,
          includeCount: true,
          pageSize: DEFAULT_PAGE,
          sortField: 'taxDeductionAccountNumber',
          sortOrder: 'ASC',
        },
        requestFilter: [],
        isReportDownload: true,
        searchCOList: getValidCriteria(searchCriteria),
      }

      const blob = await downloadTanMasterExcel(payload)
      saveBlobAsFile(blob, 'tan_master.xlsx')

    } catch (error) {
      console.error('Failed to download TAN Master Excel:', error)
    } finally {
      setStartDownloading(false)
    }
  }

  const handlePageChange = page => {
    setCurrentPage(page)
    fetchTanData(page, itemsPerPage, getValidCriteria(searchCriteria))
  }

  const handleItemsPerPageChange = newItemsPerPage => {
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1)
    fetchTanData(1, newItemsPerPage, getValidCriteria(searchCriteria))
  }

  // search
  const handleSearch = event => {
    setSearchOpen(!searchOpen)
    setAnchorEl(event.currentTarget)
  }

  const handleApplySearch = async searchList => {
    // Map fieldName to field for UI
    const mappedCriteria = searchList.map((item, idx) => ({
      ...item,
      field: item.fieldName || item.field || '',
      id: idx + 1,
      logicalOperator: item.logicalOperator || (idx === 0 ? 'Where' : 'And'),
    }))
    setSearchCriteria(mappedCriteria)
    setCurrentPage(1)
    await fetchTanData(1, itemsPerPage, searchList)
    setSearchOpen(false)
  }

  const handleResetSearch = async () => {
    setSearchCriteria([
      {
        id: 1,
        logicalOperator: 'Where',
        field: searchFields[0]?.id || '',
        value: '',
      },
    ])
    setCurrentPage(1)
    await fetchTanData(1, itemsPerPage, [])
    setSearchOpen(false)
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: '32px',
          py: '12px',
        }}
      >
        {/* Left side - Title */}
        <Typography
          variant="h6"
          sx={{
            fontSize: '20px',
            color: 'var(--forbrand-secondary)',
            fontWeight: 500,

          }}
        >
          Tan Master
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={GLOBAL_CONSTANTS.DOWNLOAD_EXCEL}>
            <span className="disabled:cursor-not-allowed">
              <IconButton
                onClick={handleDownload}
                disabled={loading || startDownloading}

              >
                <DownloadIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title={GLOBAL_CONSTANTS.SEARCH}>
            <IconButton
              onClick={handleSearch}

            >
              <SearchIconBtn />
            </IconButton>
          </Tooltip>
          {roleConfig.canEdit && (
            <>
              <Typography
                sx={{
                  height: '16px',
                  borderLeft: '1px solid var(--forbrand-color)',
                  marginRight: '6px',
                }}
              ></Typography>

              <Button
                variant="contained"
                onClick={handleAddTAN}
                sx={{
                  backgroundColor: 'var( --forbrand-bg-variant)',
                  color: 'var(--forbrand-brand-varient)',
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '16px',
                }}
              >
                Create TAN
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Table */}
      <Box>
        <CommonTable
          columns={columns}
          data={tanData}
          loading={loading}
          onEdit={roleConfig.isEditTan && handleEdit}
          onDelete={roleConfig.allScreens && handleDelete}
          emptyMessage="No TAN master data found"
          showActions={roleConfig.isEditTan}
          showFooter={true}
          totalItems={totalItems}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          showItemsPerPage={true}
          showTotalItems={true}
          showPageNumbers={true}
        />
      </Box>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Are you sure you want to delete this TAN?"
        message={`Are you sure you want to delete the specific TAN entry from the system. Doing so will remove all the Transactional data (if any) associated with this TAN?`}
        confirmText="Delete"
        itemType="tan"
        type="delete"
      />

      <Search
        open={searchOpen}
        onClose={handleCloseSearch}
        onApply={handleApplySearch}
        onReset={handleResetSearch}
        anchorEl={anchorEl}
        fields={searchFields}
        placement="bottom-start"
        setSearchCriteria={setSearchCriteria}
        searchCriteria={searchCriteria}
      />
    </Box>
  )
}

export default TAN
